import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import NativeSelect from "@mui/material/NativeSelect";
import Pagination from "@mui/material/Pagination";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { formatDate, getUserDisplayName } from "@bw/shared/utils";

import { PlatformPage } from "@/components/PlatformPage";

import {
  banUser,
  listUsers,
  sendPasswordReset,
  type PlatformUser,
  unbanUser,
} from "../api";

const PAGE_SIZE = 20;

function truncateUid(uid: string) {
  if (uid.length <= 14) return uid;
  return `${uid.slice(0, 6)}…${uid.slice(-4)}`;
}

function roleLabel(role: string) {
  return role.replace(/_/g, " ");
}

function statusLabel(status: string) {
  const normalized = roleLabel(status.trim());
  return normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : status;
}

function statusColor(status: string) {
  if (status === "active") return "success.main";
  if (status === "banned") return "error.main";
  if (status.startsWith("pending")) return "warning.main";
  return "text.primary";
}

function copyToClipboard(text: string) {
  if (navigator?.clipboard?.writeText) {
    void navigator.clipboard.writeText(text);
  }
}

function paginationLabel(type: string, page: number | null): string {
  if (type === "next") return "Next";
  if (type === "previous") return "Previous";
  if (type === "first") return "First";
  if (type === "last") return "Last";
  return page === null ? "More pages" : `Page ${page}`;
}

function BanUserDialog({
  user,
  reason,
  expiresAt,
  saving,
  onReasonChange,
  onExpiresChange,
  onClose,
  onConfirm,
}: {
  user: PlatformUser | null;
  reason: string;
  expiresAt: string;
  saving: boolean;
  onReasonChange: (value: string) => void;
  onExpiresChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Ban User
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
          {user ? getUserDisplayName(user.display_name, user.uid) : ""}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            label="Reason"
            size="small"
            multiline
            minRows={4}
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            placeholder="Document why this account is being banned"
          />
          <TextField
            type="datetime-local"
            label="Expiry"
            size="small"
            value={expiresAt}
            onChange={(event) => onExpiresChange(event.target.value)}
            helperText="Leave blank for a permanent ban."
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button type="button" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          color="error"
          variant="contained"
          size="small"
          disabled={saving || !reason.trim()}
          onClick={onConfirm}
        >
          {saving ? "Banning..." : "Ban User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function UsersListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [offset, setOffset] = useState(0);
  const [banTarget, setBanTarget] = useState<PlatformUser | null>(null);
  const [banReason, setBanReason] = useState("");
  const [banExpiresAt, setBanExpiresAt] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["platform", "users", debouncedSearch, statusFilter, offset],
    queryFn: () =>
      listUsers({
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const banMutation = useMutation({
    mutationFn: async () => {
      if (!banTarget) throw new Error("Missing target user");
      return banUser(
        banTarget.uid,
        banReason.trim(),
        banExpiresAt ? new Date(banExpiresAt).toISOString() : null,
      );
    },
    onSuccess: () => {
      setBanTarget(null);
      setBanReason("");
      setBanExpiresAt("");
      queryClient.invalidateQueries({ queryKey: ["platform", "users"] });
    },
  });

  const unbanMutation = useMutation({
    mutationFn: (userId: string) => unbanUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platform", "users"] });
    },
  });

  const resetMutation = useMutation({
    mutationFn: (email: string) => sendPasswordReset(email),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PlatformPage title="Users">
      <Stack spacing={2.5}>
        <Paper
          component="section"
          aria-label="User filters"
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(280px, 2fr) minmax(190px, 1fr)",
              },
              gap: 1.5,
              alignItems: "end",
            }}
          >
            <TextField
              type="text"
              size="small"
              label="Search users"
              placeholder="Search name, email, or uid..."
              value={search}
              slotProps={{ htmlInput: { "aria-label": "Search users" } }}
              onChange={(event) => {
                setSearch(event.target.value);
                setOffset(0);
              }}
            />
            <FormControl size="small" variant="standard">
              <InputLabel shrink htmlFor="user-status-filter">
                Status filter
              </InputLabel>
              <NativeSelect
                value={statusFilter}
                inputProps={{
                  id: "user-status-filter",
                  "aria-label": "Status filter",
                }}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setOffset(0);
                }}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="banned">Banned</option>
                <option value="pending_parental_consent">
                  Pending parental consent
                </option>
              </NativeSelect>
            </FormControl>
          </Box>
        </Paper>

        {isError ? (
          <Alert severity="error" role="alert">
            Could not load users. Try again.
          </Alert>
        ) : isLoading ? (
          <Box
            role="status"
            aria-label="Loading users"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 8 }}
          >
            <CircularProgress size={36} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            role="status"
            aria-live="polite"
            sx={{ py: 6, textAlign: "center" }}
          >
            <Typography color="text.secondary">
              No users matched this view
            </Typography>
          </Box>
        ) : (
          <>
            <Typography color="text.secondary" variant="body2">
              {total.toLocaleString()} {total === 1 ? "user" : "users"}
            </Typography>

            <Paper variant="outlined">
              <TableContainer
                role="region"
                aria-label="Users"
                tabIndex={0}
                sx={{
                  overflowX: "auto",
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                  },
                }}
              >
                <Table
                  size="small"
                  aria-label="Users"
                  sx={{
                    minWidth: 0,
                    tableLayout: "fixed",
                  }}
                >
                  <TableHead
                    sx={{
                      display: { xs: "block", lg: "table-header-group" },
                    }}
                  >
                    <TableRow
                      sx={{
                        display: { xs: "grid", lg: "table-row" },
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr) minmax(64px, 0.45fr) minmax(104px, auto)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          gridColumn: { xs: "1", lg: "auto" },
                          gridRow: { xs: "1", lg: "auto" },
                          px: { xs: 1, lg: 2 },
                          width: { lg: "20%" },
                        }}
                      >
                        User
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: "none", lg: "table-cell" },
                          width: { lg: "24%" },
                        }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{
                          gridColumn: { xs: "2", lg: "auto" },
                          gridRow: { xs: "1", lg: "auto" },
                          px: { xs: 1, lg: 2 },
                          width: { lg: "12%" },
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: "none", lg: "table-cell" },
                          width: { lg: "15%" },
                        }}
                      >
                        Roles
                      </TableCell>
                      <TableCell
                        sx={{
                          display: { xs: "none", lg: "table-cell" },
                          width: { lg: "11%" },
                        }}
                      >
                        Created
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          gridColumn: { xs: "3", lg: "auto" },
                          gridRow: { xs: "1", lg: "auto" },
                          px: { xs: 1, lg: 2 },
                          width: { lg: "18%" },
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{ display: { xs: "block", lg: "table-row-group" } }}
                  >
                    {items.map((user) => (
                      <TableRow
                        key={user.uid}
                        hover
                        sx={{
                          borderBottom: { xs: 1, lg: 0 },
                          borderColor: "divider",
                          display: { xs: "grid", lg: "table-row" },
                          gridTemplateColumns: {
                            xs: "minmax(0, 1fr) minmax(64px, 0.45fr) minmax(104px, auto)",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            borderBottom: { xs: 0, lg: 1 },
                            borderColor: "divider",
                            display: { xs: "block", lg: "table-cell" },
                            gridColumn: { xs: "1", lg: "auto" },
                            gridRow: { xs: "1", lg: "auto" },
                            px: { xs: 1, lg: 2 },
                            py: { xs: 1, lg: 0.75 },
                          }}
                        >
                          <Stack spacing={0.25}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, overflowWrap: "anywhere" }}
                            >
                              {getUserDisplayName(user.display_name, user.uid)}
                            </Typography>
                            <Stack
                              direction={{ xs: "column", lg: "row" }}
                              spacing={0.75}
                              sx={{
                                alignItems: { xs: "flex-start", lg: "center" },
                              }}
                            >
                              <Typography
                                title={user.uid}
                                color="text.secondary"
                                variant="caption"
                                sx={{
                                  fontFamily: "monospace",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {truncateUid(user.uid)}
                              </Typography>
                              <Button
                                type="button"
                                size="small"
                                onClick={() => copyToClipboard(user.uid)}
                                sx={{ minWidth: 0, px: 0.5 }}
                              >
                                Copy ID
                              </Button>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: { xs: 0, lg: 1 },
                            borderColor: "divider",
                            display: { xs: "block", lg: "table-cell" },
                            gridColumn: { xs: "1 / -1", lg: "auto" },
                            gridRow: { xs: "2", lg: "auto" },
                            px: { xs: 1, lg: 2 },
                            py: { xs: 0.75, lg: 0.75 },
                          }}
                        >
                          <Typography
                            color="text.secondary"
                            variant="overline"
                            sx={{ display: { xs: "block", lg: "none" } }}
                          >
                            Email
                          </Typography>
                          {user.email ? (
                            <Stack
                              direction={{ xs: "row", lg: "column" }}
                              spacing={0.5}
                              useFlexGap
                              sx={{
                                alignItems: { xs: "center", lg: "flex-start" },
                                flexWrap: "wrap",
                                justifyContent: {
                                  xs: "space-between",
                                  lg: "flex-start",
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ overflowWrap: "anywhere" }}
                              >
                                {user.email}
                              </Typography>
                              <Button
                                type="button"
                                size="small"
                                onClick={() =>
                                  resetMutation.mutate(user.email!)
                                }
                                sx={{
                                  alignSelf: {
                                    xs: "center",
                                    lg: "flex-start",
                                  },
                                  px: 0,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Send reset link
                              </Button>
                            </Stack>
                          ) : (
                            <Typography color="text.secondary" variant="body2">
                              Not available
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: { xs: 0, lg: 1 },
                            borderColor: "divider",
                            display: { xs: "block", lg: "table-cell" },
                            gridColumn: { xs: "2", lg: "auto" },
                            gridRow: { xs: "1", lg: "auto" },
                            px: { xs: 1, lg: 2 },
                            py: { xs: 1, lg: 0.75 },
                          }}
                        >
                          <Box sx={{ display: { xs: "none", lg: "block" } }}>
                            <Typography
                              color={statusColor(user.status)}
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                overflow: "visible",
                                overflowWrap: "anywhere",
                                textOverflow: "clip",
                                whiteSpace: "normal",
                              }}
                            >
                              {statusLabel(user.status)}
                            </Typography>
                          </Box>
                          <Typography
                            color="text.secondary"
                            variant="caption"
                            sx={{
                              display: { xs: "block", lg: "none" },
                              overflowWrap: "anywhere",
                              textTransform: "capitalize",
                            }}
                          >
                            {statusLabel(user.status)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: { xs: 0, lg: 1 },
                            borderColor: "divider",
                            display: { xs: "block", lg: "table-cell" },
                            gridColumn: { xs: "1 / -1", lg: "auto" },
                            gridRow: { xs: "3", lg: "auto" },
                            px: { xs: 1, lg: 2 },
                            py: { xs: 0.75, lg: 0.75 },
                          }}
                        >
                          <Typography
                            color="text.secondary"
                            variant="overline"
                            sx={{ display: { xs: "block", lg: "none" } }}
                          >
                            Roles
                          </Typography>
                          {user.roles.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                              No roles
                            </Typography>
                          ) : (
                            <Stack
                              direction="row"
                              spacing={0.5}
                              useFlexGap
                              sx={{ flexWrap: "wrap" }}
                            >
                              {user.roles.map((role) => (
                                <Chip
                                  key={role}
                                  size="small"
                                  variant="outlined"
                                  label={roleLabel(role)}
                                />
                              ))}
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            borderBottom: { xs: 0, lg: 1 },
                            borderColor: "divider",
                            display: { xs: "block", lg: "table-cell" },
                            gridColumn: { xs: "1 / -1", lg: "auto" },
                            gridRow: { xs: "4", lg: "auto" },
                            px: { xs: 1, lg: 2 },
                            py: { xs: 0.75, lg: 0.75 },
                          }}
                        >
                          <Typography
                            color="text.secondary"
                            variant="overline"
                            sx={{ display: { xs: "block", lg: "none" } }}
                          >
                            Created
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(user.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            borderBottom: { xs: 0, lg: 1 },
                            borderColor: "divider",
                            display: { xs: "block", lg: "table-cell" },
                            gridColumn: { xs: "3", lg: "auto" },
                            gridRow: { xs: "1", lg: "auto" },
                            px: { xs: 1, lg: 2 },
                            py: { xs: 1, lg: 0.75 },
                          }}
                        >
                          <Stack
                            direction={{ xs: "column", lg: "row" }}
                            spacing={0.5}
                            sx={{
                              alignItems: { xs: "stretch", lg: "center" },
                              justifyContent: "flex-end",
                            }}
                          >
                            <Button
                              type="button"
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/users/${user.uid}`)}
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              View details
                            </Button>
                            {user.status === "banned" ? (
                              <Button
                                type="button"
                                color="success"
                                size="small"
                                onClick={() => unbanMutation.mutate(user.uid)}
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                Unban
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                color="error"
                                size="small"
                                onClick={() => setBanTarget(user)}
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                Ban
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {total > PAGE_SIZE && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{
                    alignItems: { xs: "flex-start", sm: "center" },
                    borderTop: 1,
                    borderColor: "divider",
                    justifyContent: "space-between",
                    p: 1.5,
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    Page {page} of {pageCount}
                  </Typography>
                  <Pagination
                    page={page}
                    count={pageCount}
                    size="small"
                    siblingCount={0}
                    boundaryCount={1}
                    getItemAriaLabel={paginationLabel}
                    onChange={(_, nextPage) =>
                      setOffset((nextPage - 1) * PAGE_SIZE)
                    }
                  />
                </Stack>
              )}
            </Paper>
          </>
        )}

        <BanUserDialog
          user={banTarget}
          reason={banReason}
          expiresAt={banExpiresAt}
          saving={banMutation.isPending}
          onReasonChange={setBanReason}
          onExpiresChange={setBanExpiresAt}
          onClose={() => {
            if (banMutation.isPending) return;
            setBanTarget(null);
            setBanReason("");
            setBanExpiresAt("");
          }}
          onConfirm={() => banMutation.mutate()}
        />
      </Stack>
    </PlatformPage>
  );
}
